import React, { useEffect, useMemo, useState } from 'react';

import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import SearchIcon from '@material-ui/icons/Search';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import Table from 'components/Table';
import customerExitApi, { CustomerExitAuditLog, CustomerExitSummary } from 'config/api/customerExit/customerExit';
import customer_exit_messages from 'messages/customer_exit_messages';

type Message = readonly string[];

const useStyles = makeStyles(theme => ({
  header: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing(2),
    padding: theme.spacing(5, 0, 3),
    borderBottom: `3px solid ${theme.palette.primary.light}`,
  },
  searchRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(240px, 520px) auto',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  section: {
    marginTop: theme.spacing(4),
  },
  panel: {
    padding: theme.spacing(3),
    borderRadius: 8,
  },
  detailHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
  },
  facts: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(160px, 1fr))',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(2, minmax(140px, 1fr))',
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  fact: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    minHeight: 78,
  },
  muted: {
    color: theme.palette.text.secondary,
  },
}));

const formatDate = (value?: string | null) => (value ? format(new Date(value), 'yyyy-MM-dd HH:mm') : '-');

const PocketSafeCustomerExitPage = () => {
  const styles = useStyles();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const translate = (message: Message) => t(message[0], message[1] || '');
  const [search, setSearch] = useState('');
  const [customers, setCustomers] = useState<CustomerExitSummary[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerExitSummary | null>(null);
  const [auditLogs, setAuditLogs] = useState<CustomerExitAuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [creatingExport, setCreatingExport] = useState(false);

  const loadAuditLogs = async (organizationId?: number) => {
    const data = await customerExitApi.getAuditLogs(organizationId);
    setAuditLogs(data.results);
  };

  useEffect(() => {
    loadAuditLogs();
  }, []);

  const handleSearch = async () => {
    setLoading(true);
    try {
      const data = await customerExitApi.searchCustomers(search.trim());
      setCustomers(data.results);
      setSelectedCustomer(null);
      await loadAuditLogs();
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCustomer = async (customer: CustomerExitSummary) => {
    setLoading(true);
    try {
      const detail = await customerExitApi.getCustomer(customer.id);
      setSelectedCustomer(detail);
      await loadAuditLogs(customer.id);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateExport = async () => {
    if (!selectedCustomer || !comment.trim()) return;
    setCreatingExport(true);
    try {
      const exportResult = await customerExitApi.createExport(selectedCustomer.id, comment.trim());
      await customerExitApi.downloadExport(exportResult.id, `customer-exit-${selectedCustomer.id}.zip`);
      enqueueSnackbar(translate(customer_exit_messages.export_created), { variant: 'success' });
      setComment('');
      setExportDialogOpen(false);
      await loadAuditLogs(selectedCustomer.id);
    } finally {
      setCreatingExport(false);
    }
  };

  const customerColumns = useMemo(
    () => [
      {
        id: 'organization',
        label: translate(customer_exit_messages.organization),
        get: (row: CustomerExitSummary) => row.organization,
        width: '25%',
      },
      {
        id: 'organization_number',
        label: translate(customer_exit_messages.organization_number),
        get: (row: CustomerExitSummary) => row.organization_number || '-',
        width: '15%',
      },
      { id: 'status', label: translate(customer_exit_messages.status), get: (row: CustomerExitSummary) => row.status, width: '10%' },
      { id: 'assets', label: translate(customer_exit_messages.assets), get: (row: CustomerExitSummary) => row.assets, width: '10%' },
      {
        id: 'employees',
        label: translate(customer_exit_messages.employees),
        get: (row: CustomerExitSummary) => row.employees,
        width: '10%',
      },
      {
        id: 'action',
        label: '',
        blockSorting: true,
        get: (row: CustomerExitSummary) => (
          <Button color='primary' onClick={() => handleSelectCustomer(row)} size='small' variant='outlined'>
            {translate(customer_exit_messages.customer_details)}
          </Button>
        ),
        width: '15%',
      },
    ],
    [t],
  );

  const auditColumns = useMemo(
    () => [
      {
        id: 'created_at',
        label: translate(customer_exit_messages.timestamp),
        get: (row: CustomerExitAuditLog) => formatDate(row.created_at),
        width: '15%',
      },
      { id: 'action', label: translate(customer_exit_messages.action), get: (row: CustomerExitAuditLog) => row.action, width: '15%' },
      {
        id: 'organization',
        label: translate(customer_exit_messages.organization),
        get: (row: CustomerExitAuditLog) => row.organization_name || '-',
        width: '20%',
      },
      {
        id: 'performed_by',
        label: translate(customer_exit_messages.performed_by),
        get: (row: CustomerExitAuditLog) => row.performed_by || '-',
        width: '20%',
      },
      { id: 'query', label: translate(customer_exit_messages.query), get: (row: CustomerExitAuditLog) => row.query || '-', width: '15%' },
      {
        id: 'export_id',
        label: translate(customer_exit_messages.export_id),
        get: (row: CustomerExitAuditLog) => row.export_id || '-',
        width: '8%',
      },
      {
        id: 'comment',
        label: translate(customer_exit_messages.comment),
        get: (row: CustomerExitAuditLog) => row.comment || '-',
        width: '20%',
      },
    ],
    [t],
  );

  const countFacts: [Message, number][] = selectedCustomer
    ? [
        [customer_exit_messages.action_templates, selectedCustomer.action_templates],
        [customer_exit_messages.asset_action_instances, selectedCustomer.asset_action_instances],
        [customer_exit_messages.incidents, selectedCustomer.incidents],
        [customer_exit_messages.dsar_cases, selectedCustomer.dsar_cases],
        [customer_exit_messages.dsar_asset_cases, selectedCustomer.dsar_asset_cases],
        [customer_exit_messages.assets, selectedCustomer.assets],
        [customer_exit_messages.employees, selectedCustomer.employees],
      ]
    : [];

  return (
    <Container>
      <header className={styles.header}>
        <div>
          <Typography component='h1' variant='h2'>
            {translate(customer_exit_messages.title)}
          </Typography>
          <Typography variant='body1'>{translate(customer_exit_messages.description)}</Typography>
        </div>
        <div className={styles.searchRow}>
          <TextField
            fullWidth
            label={translate(customer_exit_messages.search_label)}
            onChange={event => setSearch(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') handleSearch();
            }}
            size='small'
            value={search}
            variant='outlined'
          />
          <Button
            color='primary'
            disabled={loading || !search.trim()}
            onClick={handleSearch}
            startIcon={<SearchIcon />}
            variant='contained'
          >
            {translate(customer_exit_messages.search_button)}
          </Button>
        </div>
      </header>

      <section className={styles.section}>
        <Table clickableRow={false} data={customers} dataMapping={customerColumns} enableSearch={false} refetching={loading} />
      </section>

      <section className={styles.section}>
        <Paper className={styles.panel} elevation={0} variant='outlined'>
          {selectedCustomer ? (
            <>
              <div className={styles.detailHeader}>
                <div>
                  <Typography component='h2' variant='h4'>
                    {selectedCustomer.organization}
                  </Typography>
                  <Typography className={styles.muted} variant='body2'>
                    {selectedCustomer.organization_number || '-'} · {selectedCustomer.status} · {formatDate(selectedCustomer.created)}
                  </Typography>
                </div>
                <Button color='secondary' onClick={() => setExportDialogOpen(true)} startIcon={<GetAppIcon />} variant='contained'>
                  {translate(customer_exit_messages.create_export)}
                </Button>
              </div>
              <Grid container spacing={2}>
                <Grid item sm={6} xs={12}>
                  <Typography className={styles.muted} variant='caption'>
                    {translate(customer_exit_messages.contact_name)}
                  </Typography>
                  <Typography>{selectedCustomer.contact_name || '-'}</Typography>
                </Grid>
                <Grid item sm={6} xs={12}>
                  <Typography className={styles.muted} variant='caption'>
                    {translate(customer_exit_messages.contact_email)}
                  </Typography>
                  <Typography>{selectedCustomer.contact_email || '-'}</Typography>
                </Grid>
              </Grid>
              <div className={styles.facts}>
                {countFacts.map(([label, value]) => (
                  <div key={label[0]} className={styles.fact}>
                    <Typography className={styles.muted} variant='caption'>
                      {translate(label)}
                    </Typography>
                    <Typography variant='h4'>{value}</Typography>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <Typography className={styles.muted}>{translate(customer_exit_messages.no_customer_selected)}</Typography>
          )}
        </Paper>
      </section>

      <section className={styles.section}>
        <Typography component='h2' variant='h4'>
          {translate(customer_exit_messages.activity_log)}
        </Typography>
        <Table clickableRow={false} data={auditLogs} dataMapping={auditColumns} enableSearch={false} refetching={loading} />
      </section>

      <Dialog fullWidth maxWidth='sm' onClose={() => setExportDialogOpen(false)} open={exportDialogOpen}>
        <DialogTitle>{translate(customer_exit_messages.export_dialog_title)}</DialogTitle>
        <DialogContent>
          <Typography paragraph>{translate(customer_exit_messages.export_dialog_text)}</Typography>
          <TextField
            autoFocus
            fullWidth
            label={translate(customer_exit_messages.comment)}
            minRows={4}
            multiline
            onChange={event => setComment(event.target.value)}
            required
            value={comment}
            variant='outlined'
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>{translate(customer_exit_messages.cancel)}</Button>
          <Button
            color='secondary'
            disabled={creatingExport || !comment.trim()}
            onClick={handleCreateExport}
            startIcon={<GetAppIcon />}
            variant='contained'
          >
            {translate(customer_exit_messages.confirm)}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PocketSafeCustomerExitPage;
